import React from 'react';

import { cn } from '../../../../../lib/utils';
import { CPT_TASK_LOG_TONE, CptViewTone } from './constants';

interface TimelineViewProps {
    value: any[];
    sumField: string;
    max?: number;
    thresholds?: number[];
    label?: string;
    tone?: CptViewTone;
}

export const TimelineView: React.FC<TimelineViewProps> = ({
    value = [],
    sumField = 'minutes',
    max = 120,
    thresholds = [8, 31, 61, 91],
    label = 'Activation Timeline',
    tone = 'default',
}) => {
    const total = value.reduce(
        (sum, item) => sum + (Number(item[sumField]) || 0),
        0
    );
    const safeMax = max > 0 ? max : 1;
    const progressPercent = Math.min((total / safeMax) * 100, 100);
    const isCptTaskLogTone = tone === CPT_TASK_LOG_TONE;
    const currentMarkerLabelPositionClassName =
        progressPercent >= 96
            ? 'right-0 text-right'
            : progressPercent <= 4
              ? 'left-0 text-left'
              : 'left-1/2 -translate-x-1/2 text-center';

    return (
        <div className="rounded-xl border border-border/45 bg-background/15 px-4 pb-6 pt-8 dark:border-white/10 dark:bg-[rgba(17,17,17,0.52)]">
            <div className="relative h-14">
                <div className="absolute inset-0 overflow-hidden rounded-lg border border-border/50 bg-muted/30 dark:border-white/10 dark:bg-[var(--main-grey)]">
                    {total > 0 && (
                        <div
                            className={cn(
                                'absolute inset-y-0 left-0 bg-white/[0.08]',
                                isCptTaskLogTone && 'bg-[#566D98]'
                            )}
                            style={{
                                width: `${progressPercent}%`,
                            }}
                        />
                    )}
                </div>

                {/* Current time marker */}
                {total > 0 && (
                    <div
                        className={cn(
                            'absolute inset-y-0 z-20 w-0.5 bg-white/60',
                            isCptTaskLogTone && 'bg-[#6AA2FF]'
                        )}
                        style={{
                            left: `${progressPercent}%`,
                        }}
                    >
                        <div
                            className={cn(
                                `absolute whitespace-nowrap ${currentMarkerLabelPositionClassName}`,
                                isCptTaskLogTone
                                    ? '-top-10 rounded-lg border border-[#315087] bg-[#1F2C49] px-3 py-1 text-sm font-semibold text-[#6AA2FF]'
                                    : '-top-8 text-xs font-bold text-foreground'
                            )}
                        >
                            {Math.floor(total)} min
                        </div>
                        <div
                            className={cn(
                                'absolute left-1/2 -translate-x-1/2 rounded-full',
                                isCptTaskLogTone
                                    ? '-top-4 h-4 w-4 bg-[#6AA2FF]'
                                    : '-top-4 h-2 w-2 bg-white/75'
                            )}
                        />
                    </div>
                )}

                {/* Threshold markers */}
                {thresholds.map((thresholdMark) => (
                    <div
                        key={thresholdMark}
                        className="absolute inset-y-0 z-10 w-px bg-border/70 dark:bg-white/10"
                        style={{
                            left: `${Math.min(
                                (thresholdMark / safeMax) * 100,
                                100
                            )}%`,
                        }}
                    >
                        <div
                            className={cn(
                                'absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs text-muted-foreground',
                                isCptTaskLogTone && 'text-[#A3A3A3]'
                            )}
                        >
                            {thresholdMark}
                        </div>
                    </div>
                ))}

                {/* Time labels at top */}
                <div
                    className={cn(
                        'absolute -top-6 left-0 text-xs text-muted-foreground',
                        isCptTaskLogTone && 'text-[#A3A3A3]'
                    )}
                >
                    0 min
                </div>
                <div
                    className={cn(
                        'absolute -top-6 right-0 text-xs text-muted-foreground',
                        isCptTaskLogTone && 'text-[#A3A3A3]'
                    )}
                >
                    {max}+ min
                </div>
            </div>
        </div>
    );
};
